import "./campaigns.page.css";

import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { createCampaign, deleteCampaign, fetchCampaigns } from "../../clients/campaigns.client";
import CenteredContainer from "../../components/layout/CenteredContainer/CenteredContainer";
import CreateCampaignDialog from "./CreateCampaignDialog";

const CampaignsPage = () => {
  const { t } = useTranslation();
  const tokenValue = useSelector((state) => state.auth.tokenValue);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCampaigns = async () => {
      setIsLoading(true);
      const response = await fetchCampaigns(tokenValue);
      if (cancelled) return;

      setCampaigns(response.isError ? [] : response.data);
      setIsLoading(false);
    };

    loadCampaigns();
    return () => {
      cancelled = true;
    };
  }, [tokenValue]);

  const handleCreateCampaign = async ({ description, name }) => {
    const response = await createCampaign(
      {
        description,
        name,
      },
      tokenValue
    );
    if (response.isError || !response.data?.id) return;

    setCampaigns((current) => [response.data, ...current]);
    setIsCreateOpen(false);
  };

  const handleDeleteCampaign = async (campaignId) => {
    const response = await deleteCampaign(campaignId, tokenValue);
    if (response.isError) return;

    setCampaigns((current) => current.filter((campaign) => campaign.id !== campaignId));
  };

  return (
    <div className="campaigns-shell">
      <CenteredContainer>
        <main className="campaigns-page">
          <header className="campaigns-page-header">
            <div>
              <p>{t("campaigns.eyebrow")}</p>
              <h1>{t("campaigns.title")}</h1>
              <span>{t("campaigns.subtitle")}</span>
            </div>
            <button
              className="campaign-primary-button"
              type="button"
              onClick={() => setIsCreateOpen(true)}
            >
              {t("campaigns.create")}
            </button>
          </header>

          {isLoading ? (
            <section className="campaign-empty-state">
              <div className="campaign-empty-card">
                <p>{t("campaigns.loading")}</p>
              </div>
            </section>
          ) : campaigns.length === 0 ? (
            <section className="campaign-empty-state">
              <div className="campaign-empty-card">
                <h3>{t("campaigns.emptyTitle")}</h3>
                <p>{t("campaigns.emptyText")}</p>
                <div className="campaign-action-row">
                  <button
                    className="campaign-primary-button"
                    type="button"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    {t("campaigns.create")}
                  </button>
                </div>
              </div>
            </section>
          ) : (
            <section className="campaigns-grid">
              {campaigns.map((campaign) => (
                <article className="campaigns-card" key={campaign.id}>
                  <div>
                    <h2>{campaign.name}</h2>
                    <p>{campaign.description || t("campaigns.noDescription")}</p>
                  </div>
                  <div className="campaigns-meta">
                    <div>
                      <strong>{campaign.memberCharacterIds.length}</strong>
                      <span>{t("campaigns.meta.members")}</span>
                    </div>
                    <div>
                      <strong>{campaign.quests.length}</strong>
                      <span>{t("campaigns.meta.quests")}</span>
                    </div>
                    <div>
                      <strong>{campaign.encounters.length}</strong>
                      <span>{t("campaigns.meta.encounters")}</span>
                    </div>
                  </div>
                  <div className="campaign-card-actions">
                    <Link
                      className="campaign-primary-button"
                      to={`/campaigns/${campaign.id}`}
                    >
                      {t("campaigns.open")}
                    </Link>
                    <button
                      className="campaign-danger-button"
                      type="button"
                      onClick={() => handleDeleteCampaign(campaign.id)}
                    >
                      {t("campaigns.delete")}
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}
        </main>

        <CreateCampaignDialog
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreateCampaign}
        />
      </CenteredContainer>
    </div>
  );
};

export default CampaignsPage;
